import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';

interface Section {
  /** Nav label key naming the screen, so the name here is the name in the menu. */
  navKey: string;
  to?: string;
  explainers?: string[];
  guides: string[];
}

/**
 * Every CADE screen's reading guides, collected. They stand behind a help icon on
 * the screens themselves (`lib/guideDisplay.ts`); this page is where all of them
 * are open at once, for a reader who wants the method rather than the answer.
 *
 * The list is the set of `i18nKey`s the CADE pages pass to `ExplainerCard` and
 * `FieldGuide`. `MethodPage.test.tsx` checks it against the page sources, so a
 * guide added to a screen and not listed here fails there instead of vanishing
 * from the method page.
 */
const METHOD_SECTIONS: Section[] = [
  {
    navKey: 'cadeOverview',
    to: '/cade/overview',
    explainers: ['cade:overview.explainer'],
    guides: [
      'cade:overview.guide.corpus',
      'cade:overview.guide.caseTypes',
      'cade:overview.guide.archiveByYear',
      'cade:overview.guide.trend',
      'cade:overview.guide.distributions',
      'cade:overview.guide.refusals',
      'cade:overview.guide.votes',
    ],
  },
  {
    navKey: 'cadeDecisions',
    to: '/cade/decisions',
    explainers: ['cade:decisions.explainer', 'cade:decision.explainer'],
    guides: ['cade:decisions.guide', 'cade:decision.guide'],
  },
  {
    navKey: 'cadeCases',
    to: '/cade/cases',
    explainers: ['cade:cases.explainer', 'cade:case.explainer'],
    guides: [
      'cade:cases.guide.columns',
      'cade:cases.guide.panels',
      'cade:cases.guide.registry',
      'cade:case.guide.decision',
      'cade:case.guide.comparables',
      'cade:case.guide.attribution',
    ],
  },
  {
    navKey: 'cadeSessions',
    to: '/cade/sessions',
    explainers: ['cade:sessions.explainer'],
    guides: ['cade:sessions.guide'],
  },
  {
    navKey: 'cadeActivity',
    to: '/cade/activity',
    explainers: ['cade:activity.explainer'],
    guides: [
      'cade:activity.guide.universe',
      'cade:activity.guide.columns',
      'cade:activity.guide.chart',
      'cade:activity.guide.duration',
      'cade:activity.guide.fines',
      'cade:register.flow.guide',
    ],
  },
  {
    navKey: 'cadeCommissioners',
    to: '/cade/commissioners',
    explainers: ['cade:commissioners.explainer'],
    guides: ['cade:commissioners.guide', 'cade:commissioners.rosterGuide'],
  },
  {
    navKey: 'cadeSanctions',
    to: '/cade/sanctions',
    explainers: ['cade:sanctions.explainer'],
    guides: [
      'cade:sanctions.guide.distribution',
      'cade:sanctions.guide.ledger',
      'cade:sanctions.guide.provenance',
    ],
  },
  {
    navKey: 'cadeParties',
    to: '/cade/parties',
    explainers: ['cade:parties.explainer'],
    guides: ['cade:parties.guide.list', 'cade:parties.guide.record'],
  },
  {
    navKey: 'cadeConsistency',
    to: '/cade/consistency',
    explainers: ['cade:consistency.explainer'],
    guides: ['cade:consistency.guide.funnel', 'cade:consistency.guide.adjudication'],
  },
  {
    navKey: 'cadeVotes',
    to: '/cade/votes',
    explainers: ['cade:votes.explainer'],
    guides: ['cade:votes.guide.reach', 'cade:votes.guide.columns'],
  },
  {
    navKey: 'cadeConselho',
    to: '/cade/conselho',
    explainers: ['cade:conselho.explainer'],
    guides: ['cade:conselho.guide', 'cade:conselho.itemsGuide'],
  },
  {
    navKey: 'cadeAudio',
    to: '/cade/audio',
    explainers: ['cade:audio.explainer'],
    guides: ['cade:audio.guide'],
  },
  {
    navKey: 'cadeConselheiro',
    explainers: ['cade:conselheiro.explainer'],
    guides: ['cade:conselheiro.guide.record', 'cade:conselheiro.guide.comparison'],
  },
  {
    navKey: 'cadeVocabulary',
    guides: ['cade:vocabulary.caseTypes'],
  },
];

/** The three analyses that answer only part of what they were built to, and say so. */
const LIMITED: { to: string; navKey: string; noteKey: string }[] = [
  { to: '/cade/consistency', navKey: 'cadeConsistency', noteKey: 'cade:method.limited.consistency' },
  { to: '/cade/votes', navKey: 'cadeVotes', noteKey: 'cade:method.limited.votes' },
  { to: '/cade/conselho', navKey: 'cadeConselho', noteKey: 'cade:method.limited.conselho' },
  { to: '/cade/audio', navKey: 'cadeAudio', noteKey: 'cade:method.limited.audio' },
];

export function CadeMethodPage() {
  const { t } = useTranslation(['cade', 'common']);

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:method.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:method.intro')}</p>
      </header>

      <section className="space-y-2" aria-labelledby="method-limited">
        <h2 id="method-limited" className="text-sm font-semibold text-foreground">
          {t('cade:method.limited.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('cade:method.limited.intro')}</p>
        <ul className="space-y-2">
          {LIMITED.map((entry) => (
            <li key={entry.to} className="rounded-md border p-3 text-sm">
              <Link to={entry.to} className="font-medium text-primary underline-offset-4 hover:underline">
                {t(`common:nav.${entry.navKey}`)}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{t(entry.noteKey)}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-4" aria-labelledby="method-guides">
        <h2 id="method-guides" className="text-sm font-semibold text-foreground">
          {t('cade:method.guides.title')}
        </h2>
        {METHOD_SECTIONS.map((section) => (
          <div key={section.navKey} className="space-y-2 rounded-md border p-3">
            <h3 className="text-sm font-medium text-foreground">
              {section.to ? (
                <Link to={section.to} className="text-primary underline-offset-4 hover:underline">
                  {t(`cade:method.screens.${section.navKey}`)}
                </Link>
              ) : (
                t(`cade:method.screens.${section.navKey}`)
              )}
            </h3>
            {(section.explainers ?? []).map((key) => (
              <ExplainerCard key={key} i18nKey={key} inline />
            ))}
            {section.guides.map((key) => (
              <FieldGuide key={key} i18nKey={key} inline />
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}
